/**
 * End of turn from a recognizer's partials: the creator has finished when the text stops changing for a
 * moment (900 ms; 1.5 s after a filler or a dangling "and", 700 ms after a question) and the mic level has
 * gone quiet too, since partials can lag the voice. A turn never runs past 45 s.
 */
const FILLER = /\b(?:and|but|so|because|or|um+|uh+|er+|like|the|a|an|to|with|of|for|my|your)[,.…]*$/i

export function endSilenceFor(text: string): number {
    const t = text.trim()
    if (/\?["')\]]*$/.test(t)) return 700
    if (FILLER.test(t)) return 1500
    return 900
}

export interface TurnDetectorOptions {
    /** Mic level (0..1) above which the creator is still talking. */
    voiceLevel?: number
    maxTurnMs?: number
}

export class TurnDetector {
    private text = ''
    private changedAt = 0
    private startedAt = 0
    private voiceAt = 0
    private readonly voiceLevel: number
    private readonly maxTurnMs: number

    constructor(opts: TurnDetectorOptions = {}) {
        this.voiceLevel = opts.voiceLevel ?? 0.35
        this.maxTurnMs = opts.maxTurnMs ?? 45_000
    }

    get speaking(): boolean {
        return this.text.trim().length > 0
    }

    onPartial(text: string, now: number): void {
        if (text === this.text) return
        if (!this.text.trim() && text.trim()) this.startedAt = now
        this.text = text
        this.changedAt = now
    }

    onLevel(level: number, now: number): void {
        if (level >= this.voiceLevel) this.voiceAt = now
    }

    /** Has the creator finished their turn? */
    due(now: number): boolean {
        if (!this.speaking) return false
        if (now - this.startedAt >= this.maxTurnMs) return true
        const quietSince = Math.max(this.changedAt, this.voiceAt)
        return now - quietSince >= endSilenceFor(this.text)
    }

    reset(): void {
        this.text = ''
        this.changedAt = 0
        this.startedAt = 0
        this.voiceAt = 0
    }
}
